import { cn } from "@repo/shared/utils";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@repo/ui/components/Base/Dialog";
import { Tooltip } from "@repo/ui/components/Base/Tooltip";
import { CircleHelpIcon } from "lucide-react";

export const RoleDefinitionsDialog = ({
  open,
  setOpen,
  triggerStyle,
  iconStyle,
}: {
  open?: boolean;
  setOpen?: (open: boolean) => void;
  triggerStyle?: string;
  iconStyle?: string;
}) => {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!setOpen && (
        <Tooltip label="Role Permissions" asChild>
          <DialogTrigger
            className={cn(
              "w-10 h-10 flex flex-row justify-center items-center self-end",
              triggerStyle
            )}
          >
            <CircleHelpIcon className={cn("text-primary", iconStyle)} />
          </DialogTrigger>
        </Tooltip>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Definitions and Permissions of Roles</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="mb-2" />

          {/* NONE */}
          <h2 className="text-lg font-bold mb-1">None</h2>
          <p className="mb-2">
            Users with the <em>None</em> role in the organization cannot view
            pages or items unless they are on a team where they have been
            granted a higher role. In that case they can only see data for that
            team with the level of access assigned by that team.
          </p>
          <p className="mb-2">
            A team member's role can also be <em>None</em> which prevents the
            user from viewing that team's data.
          </p>
          <p className="mb-2">Examples:</p>
          <ul className="mb-2 list-disc pl-4">
            <li>
              Members of an authority who do not need to see the Borough
              Council's data or vice versa
            </li>
            <li>
              Media and TV companies that would like to receive published
              meeting notices
            </li>
          </ul>

          <hr className="my-3" />

          {/* VIEW */}
          <h2 className="text-lg font-bold mb-1">View</h2>
          <p className="mb-2">
            Users with <em>View</em> role can only view pages and items
            throughout the app. They are able to open meetings, agenda items,
            and documents to see additional related details. They are also
            allowed to view non-confidential documents.
          </p>
          <p className="mb-2">Examples:</p>
          <ul className="mb-2 list-disc pl-4">
            <li>
              Interns and employees that do not need to submit requests for a
              meeting
            </li>
          </ul>
          <p className="mb-2">
            <strong>Note of caution:</strong> Do not be too generous with
            view-permitted users. View permissions may change slightly in the
            future.
          </p>

          <hr className="my-3" />

          {/* SUBMIT */}
          <h2 className="text-lg font-bold mb-1">Submit</h2>
          <p className="mb-2">
            Users with <em>Submit</em> role can submit new agenda items to all
            teams and users. They are also able to edit these items later. In
            addition, users with <em>Submit</em> role also inherit all
            privileges of <em>View</em> role.
          </p>
          <p className="mb-2">Examples:</p>
          <ul className="mb-2 list-disc pl-4">
            <li>Department heads</li>
          </ul>

          <hr className="my-3" />

          {/* EDIT */}
          <h2 className="text-lg font-bold mb-1">Edit</h2>
          <p className="mb-2">
            Users with <em>Edit</em> role in a team can edit all agenda items,
            meetings, and documents within the team. They are also able to view
            confidential documents within the team.
          </p>
          <p className="mb-2">Examples:</p>
          <ul className="mb-2 list-disc pl-4">
            <li>Secretary assistants</li>
            <li>Agenda gatekeepers</li>
          </ul>

          <hr className="my-3" />

          {/* ADMIN */}
          <h2 className="text-lg font-bold mb-1">Admin</h2>
          <p className="mb-2">
            Users with <em>Admin</em> role have administrative privileges for
            the entire app, even if their admin role is for a specific team.
            Administrators are permitted to edit the organization details and
            manage users, teams, letterheads, agenda templates, and agenda
            section templates. Since they can manage teams, they are also
            permitted to elevate their own roles in other teams.
          </p>
          <p className="mb-2">Examples:</p>
          <ul className="mb-2 list-disc pl-4">
            <li>Secretaries</li>
            <li>Chief Clerks</li>
          </ul>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};
